"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { EVENTS as EVENT_MEDIA, mediaPath } from "@/lib/media";
const UPCOMING_EVENTS = [{
  id: "01",
  title: "Global Diaspora Conference",
  date: "October 14-16, 2026",
  location: "Suva, Fiji",
  type: "Symposium",
  image: EVENT_MEDIA.placeholder,
  imageAlt: `${EVENT_MEDIA.placeholderAlt}, Global Diaspora Conference`
}, {
  id: "02",
  title: "Archival Exhibition: The 1838 Ships",
  date: "November 5, 2026",
  location: "Port Louis, Mauritius",
  type: "Exhibition",
  image: EVENT_MEDIA.placeholder,
  imageAlt: `${EVENT_MEDIA.placeholderAlt}, Archival Exhibition`
}, {
  id: "03",
  title: "Genealogy & Lineage Workshop",
  date: "December 12, 2026",
  location: "Virtual Event",
  type: "Workshop",
  image: EVENT_MEDIA.placeholder,
  imageAlt: `${EVENT_MEDIA.placeholderAlt} — Genealogy Workshop`
}, {
  id: "04",
  title: "Girmitiya Heritage Gala",
  date: "January 20, 2027",
  location: "New Delhi, India",
  type: "Ceremony",
  image: EVENT_MEDIA.placeholder,
  imageAlt: `${EVENT_MEDIA.placeholderAlt}, Heritage Gala`
}];
const FONT_STYLE = `
    .events-grain::after {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
        pointer-events: none;
        z-index: 10;
    }
`;
const UpcomingEvents = () => {
  const $ = _c(27);
  const [hoveredIdx, setHoveredIdx] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <style>{FONT_STYLE}</style>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6"><div className="w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10.5px] tracking-[0.25em] uppercase text-accent-readable font-light">{"Gatherings & Exhibitions"}</span></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 sm:gap-8 mb-12 sm:mb-20"><div>{t1}<h2 className="font-display text-[34px] sm:text-[44px] md:text-[64px] font-light text-[#F4EFE6] leading-[1.05]">Upcoming <em className="text-[#C9A84C] italic">Events</em></h2></div><Link href="/events" className="font-body text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#F4EFE6] border border-white/20 px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-[#F4EFE6] hover:text-[#0F1C2E] transition-colors whitespace-nowrap">View Full Calendar</Link></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = UPCOMING_EVENTS[hoveredIdx];
  let t4;
  if ($[3] !== t3.image) {
    t4 = mediaPath(t3.image);
    $[3] = t3.image;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = UPCOMING_EVENTS[hoveredIdx].imageAlt || UPCOMING_EVENTS[hoveredIdx].title;
  let t6;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      scale: 1.05,
      filter: "blur(10px)"
    };
    t7 = {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)"
    };
    t8 = {
      opacity: 0,
      scale: 0.95,
      filter: "blur(10px)"
    };
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== hoveredIdx || $[10] !== t4 || $[11] !== t5) {
    t10 = <AnimatePresence mode="wait"><motion.img key={hoveredIdx} src={t4} alt={t5} initial={t6} animate={t7} exit={t8} transition={t9} className="absolute inset-0 w-full h-full object-cover grayscale-[40%] sepia-[30%]" /></AnimatePresence>;
    $[9] = hoveredIdx;
    $[10] = t4;
    $[11] = t5;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="absolute inset-3 sm:inset-4 border border-white/20 mix-blend-overlay pointer-events-none" />;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = UPCOMING_EVENTS[hoveredIdx];
  let t13;
  if ($[14] !== t12.id) {
    t13 = <span className="font-body text-[9px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] uppercase text-white/70">Plate. {t12.id}</span>;
    $[14] = t12.id;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const t14 = UPCOMING_EVENTS[hoveredIdx];
  let t15;
  if ($[16] !== t14.type) {
    t15 = <span className="font-display text-[16px] sm:text-[20px] text-[#C9A84C] italic">{t14.type}</span>;
    $[16] = t14.type;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t13 || $[19] !== t15) {
    t16 = <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 flex justify-between items-end z-10 mix-blend-difference">{t13}{t15}</div>;
    $[18] = t13;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t10 || $[22] !== t16) {
    t17 = <div className="w-full lg:w-[40%] lg:sticky lg:top-32 aspect-[16/9] sm:aspect-[4/3] lg:aspect-[3/4] relative overflow-hidden border border-white/10 bg-black/20 shadow-[0_30px_60px_rgba(0,0,0,0.5)]">{t10}{t11}{t16}</div>;
    $[21] = t10;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="w-full lg:w-[60%] flex flex-col">{UPCOMING_EVENTS.map((event, idx) => <div key={event.id} onMouseEnter={() => setHoveredIdx(idx)} className="group flex flex-col border-t border-white/10 py-6 sm:py-10 cursor-pointer relative"><div className="absolute inset-0 bg-[#F4EFE6]/[0.02] scale-y-0 group-hover:scale-y-100 origin-bottom transition-transform duration-500 -z-10" /><div className="flex flex-row items-center justify-between gap-4 px-2 sm:px-4 -mx-2 sm:-mx-4"><div className="flex-1 min-w-0"><div className="flex items-center flex-wrap gap-x-2 gap-y-1 sm:gap-4 mb-2 sm:mb-4"><span className="font-body text-[9px] sm:text-[10px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#DF6D2A]">{event.date}</span><span className="w-1 h-1 rounded-full bg-white/20 hidden sm:inline-block" /><span className="font-body text-[9px] sm:text-[10px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-white/50 group-hover:text-[#C9A84C] transition-colors">{event.location}</span></div><h3 className="font-display text-[20px] sm:text-[30px] md:text-[44px] text-[#F4EFE6] leading-[1.2] sm:leading-[1.1] group-hover:text-[#DF6D2A] transition-colors duration-500 pr-2 sm:pr-8">{event.title}</h3></div><div className="flex items-center gap-6 shrink-0"><div className="overflow-hidden h-6 hidden md:block"><span className="font-body text-[11px] tracking-[0.2em] uppercase text-[#F4EFE6] block translate-y-6 group-hover:translate-y-0 transition-transform duration-500">Register / RSVP</span></div><div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-[#DF6D2A] group-hover:border-[#DF6D2A] transition-all duration-500 relative overflow-hidden shrink-0"><span className="absolute text-white transition-transform duration-500 group-hover:translate-x-8 group-hover:-translate-y-8"><ArrowUpRight className="w-4 h-4 sm:w-6 sm:h-6" /></span><span className="absolute text-white -translate-x-8 translate-y-8 transition-transform duration-500 group-hover:translate-x-0 group-hover:translate-y-0"><ArrowUpRight className="w-4 h-4 sm:w-6 sm:h-6" /></span></div></div></div></div>)}<div className="border-t border-white/10" /></div>;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t17) {
    t19 = <>{t0}<section className="bg-[#0F1C2E] py-16 sm:py-24 md:py-40 relative overflow-hidden events-grain border-y border-[#F4EFE6]/10"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 relative z-20">{t2}<div className="flex flex-col lg:flex-row gap-8 sm:gap-12 lg:gap-24 items-start">{t17}{t18}</div></div></section></>;
    $[25] = t17;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  return t19;
};
export default UpcomingEvents;